// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { headerStyles } from "@steiner/Overrides/Widgets/MyListsDetails/MyListsDetailsHeader";
import Users from "@steiner/XDL/Icons/Users";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, linkSizeVariants, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

interface SharingStatusSharedStyles {
    sharedIcon?: IconPresentationProps;
    statusText?: TypographyProps;
}

export const sharingStatusSharedStyles: SharingStatusSharedStyles = {
    sharedIcon: {
        src: Users,
        size: linkSizeVariants.small.iconSize,
        color: "primary",
        css: css`
            margin-right: 8px;
        `,
    },
    statusText: {
        css: css`
            ${TypographyStyle} {
                font-size: ${linkSizeVariants.small.size}px;
                line-height: ${linkSizeVariants.small.lineHeight};
                font-weight: ${fontConfig.weight.semiBold};
            }
            &:hover {
                ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
            }
        `,
    },
};

headerStyles.infoGridItem = {
    width: 12,
    css: css`
        align-items: flex-end;
        flex-wrap: wrap;
        column-gap: 12px;
        ${xScreen("lte", "sm")} {
            align-items: flex-start;
        }
        ${xScreen("lte", "xs")} {
            flex-direction: column;
        }
    `,
};

headerStyles.lastUpdateText = {
    color: "common.ui4",
    size: fontConfig.fontSize.xSmall,
    italic: true,
};

headerStyles.sharingStatus = {
    sharedIcon: sharingStatusSharedStyles.sharedIcon,
    statusText: sharingStatusSharedStyles.statusText,
};

headerStyles.tagsSection = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};

headerStyles.tagsTitle = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
    transform: "capitalize",
};

headerStyles.tagsWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    `,
};

headerStyles.tag = {
    css: css`
        margin: 0;
        border-radius: 13px;
        padding: 4px 8px;
    `,
};

headerStyles.wishListHiddenText = {
    color: "common.ui4",
    size: 12,
    italic: true,
};

headerStyles.descriptionStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    heading: {
        size: fontConfig.fontSize.xSmall,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.xSmall,
        transform: "capitalize",
        css: css`
            margin-bottom: 4px;
        `,
    },
    text: {
        size: fontConfig.fontSize.xSmall,
    },
};

headerStyles.totalLabel = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
};

headerStyles.totalText = {
    size: fontConfig.fontSize.regular,
    weight: fontConfig.weight.bold,
    lineHeight: fontConfig.lineHeight.regular,
    color: "primary",
};

headerStyles.totalVatLabelText = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
};

headerStyles.totalWithoutVatText = {
    size: fontConfig.fontSize.regular,
    weight: fontConfig.weight.bold,
    lineHeight: fontConfig.lineHeight.regular,
    color: "primary",
};

headerStyles.totalWithoutVatLabelText = {
    size: fontConfig.fontSize.regular,
    weight: fontConfig.weight.bold,
    lineHeight: fontConfig.lineHeight.regular,
    color: "primary",
};

headerStyles.totalWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
            gap: 6px;
        }
    `,
};
